import { motion } from "framer-motion";
import { ArrowUpRight, Check, Clock, ArrowRight, MessageCircle } from "lucide-react";
import { SITE, SERVICES, IMAGES } from "@/data/site";
import { Reveal, Overline, MaskedLines } from "@/components/Motion";
import Seo from "@/components/Seo";

function Hero() {
  return (
    <section className="relative pt-40 pb-20 lg:pb-28 overflow-hidden bg-brand-navy">
      <div className="absolute inset-0 opacity-20">
        <img src={IMAGES.texture} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Overline className="text-brand-sky">What we do</Overline>
        </motion.div>
        <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.95] mt-6">
          <MaskedLines lines={["Three ways to bring", "clarity to your money."]} delay={0.3} />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="text-white/70 text-lg font-light mt-8 max-w-2xl leading-relaxed"
        >
          Each engagement starts with a free 30-minute conversation. Pick the service that fits where you are today — we&apos;ll take it from there.
        </motion.p>
      </div>
    </section>
  );
}

function ServiceRow({ s, i }) {
  const reversed = i % 2 === 1;
  const calendlyUrl = `${SITE.calendly}`;
  const waMsg = `Hi Avishaa, I'm interested in ${s.title}. Could you share more details?`;
  const waUrl = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(waMsg)}`;
  return (
    <Reveal>
      <div className={`grid lg:grid-cols-12 gap-8 lg:gap-14 items-center py-16 lg:py-24 border-b border-brand-border ${reversed ? "" : ""}`}>
        <div className={`lg:col-span-2 ${reversed ? "lg:order-3" : ""}`}>
          <span className="font-serif text-7xl lg:text-8xl text-brand-border select-none">{s.num}</span>
        </div>

        <div className={`lg:col-span-6 ${reversed ? "lg:order-2" : ""}`}>
          <Overline>{s.short}</Overline>
          <h2 className="font-serif text-4xl lg:text-6xl text-brand-navy font-light mt-4">{s.title}</h2>
          <p className="text-brand-muted text-lg font-light leading-relaxed mt-6 max-w-xl">{s.desc}</p>
        </div>

        <div className={`lg:col-span-4 ${reversed ? "lg:order-1" : ""}`}>
          <div className="bg-white border border-brand-border rounded-2xl p-7">
            <ul className="space-y-4">
              {s.points.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <span className="mt-1 flex-shrink-0 h-5 w-5 rounded-full bg-brand-green/10 flex items-center justify-center">
                    <Check className="h-3 w-3 text-brand-green" />
                  </span>
                  <span className="text-brand-navy/80 font-light">{p}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2 text-brand-muted text-sm mt-6 pt-6 border-t border-brand-border">
              <Clock className="h-4 w-4 text-brand-green" /> 30-minute free session
            </div>
            <a
              href={calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`calendly-btn-${s.id}`}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy text-white text-sm font-medium px-6 py-4 hover:bg-brand-green transition-colors group"
            >
              Book {s.title} <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`whatsapp-btn-${s.id}`}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#25D366] text-[#128C4B] text-sm font-medium px-6 py-3.5 hover:bg-[#25D366] hover:text-white transition-colors"
            >
              <MessageCircle className="h-4 w-4" /> Ask on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function Services() {
  return (
    <div>
      <Seo
        title="Services | Arivu Finserve — Financial, Retirement & Investment Planning"
        description="Explore Arivu Finserve's three services: Financial Planning, Retirement Planning and Investment Planning. Book a free 30-minute consultation or ask on WhatsApp."
      />
      <Hero />
      <section data-testid="services-list" className="max-w-7xl mx-auto px-6 lg:px-10">
        {SERVICES.map((s, i) => (
          <ServiceRow key={s.id} s={s} i={i} />
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <Reveal>
          <div className="bg-brand-navy rounded-3xl p-10 lg:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <img src={IMAGES.growth} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="relative z-10">
              <h3 className="font-serif text-white text-4xl lg:text-5xl font-light">Not sure where to begin?</h3>
              <p className="text-white/70 font-light mt-4 max-w-xl mx-auto">Book a free 30-minute consultation and we&apos;ll help you find the right starting point.</p>
              <a
                href={SITE.calendly}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="services-cta-btn"
                className="inline-flex items-center gap-2 mt-8 rounded-full bg-brand-accent text-white text-sm font-medium px-8 py-4 hover:bg-white hover:text-brand-navy transition-colors group"
              >
                Schedule your call <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
